import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, FileText, MessageCircle, PlayCircle, Send } from "lucide-react";
import PublicHeader from "@/components/public/PublicHeader";
import PublicFooter from "@/components/public/PublicFooter";
import { UnitGallery } from "@/components/catalog/UnitGallery";
import { YouTubeEmbed } from "@/components/catalog/YouTubeEmbed";
import { UnitStatusBadge } from "@/components/catalog/UnitStatusBadge";
import { InterestDialog } from "@/components/catalog/InterestDialog";
import { OfertaSection } from "@/components/catalog/OfertaSection";
import { PdfModal } from "@/components/catalog/PdfModal";
import { VideoModal } from "@/components/catalog/VideoModal";
import { api } from "@/lib/api";
import { CATEGORY_LABELS, CATEGORY_SLUGS_TO_KEY, formatMXN, whatsappLinkForUnit } from "@/lib/catalog";

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}

export default function CatalogDetail() {
  const { category: slug, id } = useParams();
  const category = CATEGORY_SLUGS_TO_KEY[slug];
  const [unit, setUnit] = useState(undefined);
  const [agencia, setAgencia] = useState(null);
  const [interestOpen, setInterestOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);

  useEffect(() => {
    api.get(`/catalog/units/${id}`).then((r) => setUnit(r.data)).catch(() => setUnit(null));
  }, [id]);

  // For nuevo units, resolve the partner name from the public partners endpoint.
  useEffect(() => {
    if (unit && unit.category === "nuevo" && unit.agencia_aliada_id) {
      api.get("/partners/public").then((r) => {
        const found = r.data.find((p) => p.id === unit.agencia_aliada_id);
        setAgencia(found || null);
      }).catch(() => setAgencia(null));
    }
  }, [unit]);

  if (!category) return <Navigate to="/" replace />;
  if (unit === undefined) {
    return (
      <div className="min-h-screen bg-white text-[#3E5641]">
        <PublicHeader />
        <main className="container-x pt-40 pb-24 text-sm text-[#6B7678]" data-testid="detail-loading">Cargando…</main>
        <PublicFooter />
      </div>
    );
  }
  if (unit === null || unit.category !== category) {
    return (
      <div className="min-h-screen bg-white text-[#3E5641]">
        <PublicHeader />
        <main className="container-x pt-40 pb-24" data-testid="detail-not-found">
          <p className="font-serif text-3xl text-[#3E5641]">Unidad no encontrada.</p>
          <Link to={`/inventario/${slug}`} className="btn-outline-dark mt-8 inline-flex">
            <ArrowLeft size={16} /> Volver al inventario
          </Link>
        </main>
        <PublicFooter />
      </div>
    );
  }

  const label = CATEGORY_LABELS[category];
  const isPromo = category === "promocion";
  const showPdf = !isPromo && !!unit.ficha_pdf;
  const showVideo = !isPromo && !!unit.video;
  const tags = isPromo ? [] : [
    unit.kilometraje != null ? `${Number(unit.kilometraje).toLocaleString("en-US")} km` : null,
    unit.transmision || null,
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-white text-[#3E5641]" data-testid="page-catalog-detail">
      <PublicHeader />
      <main className="container-x pt-28 pb-24 md:pt-36">
        <Link to={`/inventario/${slug}`} className="inline-flex items-center gap-2 text-sm text-[#6B7678] hover:text-[#3E5641]" data-testid="detail-back">
          <ArrowLeft size={14} /> {label}
        </Link>

        <div className="mt-6 grid gap-10 md:grid-cols-12">
          <div className="md:col-span-7">
            <UnitGallery imagenPrincipal={unit.imagen_principal} galeria={unit.galeria} titulo={unit.titulo} />
            {isPromo && unit.youtube_url && (
              <div className="mt-8">
                <p className="mono text-xs uppercase tracking-widest text-[#A0553F]">Video</p>
                <div className="mt-3"><YouTubeEmbed url={unit.youtube_url} title={unit.titulo} /></div>
              </div>
            )}
          </div>

          <div className="md:col-span-5">
            <p className="mono text-xs uppercase tracking-widest text-[#A0553F]">
              {isPromo ? label : `${label} · ${unit.marca} · ${unit.anio}`}
            </p>
            <h1 className="mt-3 font-serif text-4xl leading-tight text-[#3E5641] md:text-5xl" data-testid="detail-titulo">
              {unit.titulo}
            </h1>
            {!isPromo && (
              <p className="mt-3 text-sm text-[#6B7678]">
                {unit.marca} {unit.modelo} · {unit.version}
              </p>
            )}
            {tags.length > 0 && (
              <p className="mt-2 flex flex-wrap gap-2" data-testid="detail-tags">
                <span className="rounded-sm border border-[#3E5641]/15 bg-[#F1F3F3] px-2 py-0.5 text-xs text-[#3E5641]">{tags.join(" · ")}</span>
              </p>
            )}

            {!isPromo && (
              <div className="mt-6 flex flex-wrap items-baseline gap-4">
                {unit.oferta_vigente && unit.oferta?.precio_oferta != null ? (
                  <>
                    <p className="mono text-3xl text-[#C4674F]" data-testid="detail-precio">{formatMXN(unit.oferta.precio_oferta)}</p>
                    <p className="mono text-base text-[#6B7678] line-through" data-testid="detail-precio-lista">{formatMXN(unit.oferta.precio_lista ?? unit.precio)}</p>
                  </>
                ) : (
                  <p className="mono text-3xl text-[#3E5641]" data-testid="detail-precio">{formatMXN(unit.precio)}</p>
                )}
                <UnitStatusBadge estado={unit.estado} />
              </div>
            )}
            {isPromo && (
              <div className="mt-6"><UnitStatusBadge estado={unit.estado} /></div>
            )}

            {!isPromo && <OfertaSection unit={unit} />}

            {category === "nuevo" && agencia && (
              <div className="mt-5 border border-[#3E5641]/10 bg-[#F1F3F3] p-4">
                <p className="mono text-[10px] uppercase tracking-widest text-[#6B7678]">Agencia aliada</p>
                <p className="mt-1 text-sm text-[#3E5641]" data-testid="detail-agencia">{agencia.nombre}</p>
              </div>
            )}

            {isPromo && (unit.vigencia_inicio || unit.vigencia_fin) && (
              <div className="mt-5 border border-[#3E5641]/10 bg-[#F1F3F3] p-4">
                <p className="mono text-[10px] uppercase tracking-widest text-[#6B7678]">Vigencia</p>
                <p className="mt-1 text-sm text-[#3E5641]" data-testid="detail-vigencia">
                  {formatDate(unit.vigencia_inicio) && formatDate(unit.vigencia_fin)
                    ? `${formatDate(unit.vigencia_inicio)} — ${formatDate(unit.vigencia_fin)}`
                    : (formatDate(unit.vigencia_fin) ? `Hasta ${formatDate(unit.vigencia_fin)}` : `Desde ${formatDate(unit.vigencia_inicio)}`)}
                </p>
              </div>
            )}

            <div className="mt-8 space-y-3">
              {/* Reservado: botón "Lo quiero a crédito" junto a "Me interesa" (se construye después) */}
              <button type="button" onClick={() => setInterestOpen(true)} className="btn-primary w-full" data-testid="detail-interest-btn">
                <Send size={16} /> Me interesa
              </button>
              <a href={whatsappLinkForUnit(unit.titulo)} target="_blank" rel="noopener noreferrer" className="btn-outline-dark w-full" data-testid="detail-whatsapp-btn">
                <MessageCircle size={16} /> Contactar por WhatsApp
              </a>
            </div>

            {(showPdf || showVideo) && (
              <div className="mt-3 grid grid-cols-1 gap-3 min-[420px]:grid-cols-2" data-testid="detail-media-row">
                {showPdf && (
                  <button type="button" onClick={() => setPdfOpen(true)} className={`btn-outline-dark w-full whitespace-nowrap ${showVideo ? "" : "min-[420px]:col-span-2"}`} data-testid="detail-pdf-btn">
                    <FileText size={16} /> Detalles del auto
                  </button>
                )}
                {showVideo && (
                  <button type="button" onClick={() => setVideoOpen(true)} className={`btn-outline-dark w-full whitespace-nowrap ${showPdf ? "" : "min-[420px]:col-span-2"}`} data-testid="detail-video-btn">
                    <PlayCircle size={16} /> Video
                  </button>
                )}
              </div>
            )}

            {isPromo && unit.descripcion && (
              <div className="mt-10 border-t border-[#3E5641]/10 pt-8">
                <p className="mono text-xs uppercase tracking-widest text-[#6B7678]">Descripción</p>
                <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-[#3E5641]" data-testid="detail-descripcion">
                  {unit.descripcion}
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
      <PublicFooter />
      <InterestDialog unit={unit} open={interestOpen} onClose={() => setInterestOpen(false)} />
      {showPdf && <PdfModal open={pdfOpen} onClose={() => setPdfOpen(false)} path={unit.ficha_pdf} titulo={unit.titulo} />}
      {showVideo && <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} unitId={unit.id} video={unit.video} />}
    </div>
  );
}
